// ReceiptsPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import api from "../../../services/api";
import PageLayout from "../../../components/layout/PageLayout";
import { Header, DateRangePicker } from "../../../components/ui";
import {
  FaFileInvoice,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaUser,
  FaClock,
} from "react-icons/fa";
import styles from "./ReceiptsPage.module.css";

export default function ReceiptsPage() {
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [districtFilter, setDistrictFilter] = useState("Все");
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("cards"); // по умолчанию карточки

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const fetchReceipts = async (range = dateRange) => {
    if (!range?.from || !range?.to) return;

    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/receipts", {
        params: { startDate: range.from, endDate: range.to },
      });

      setReceipts(res.receipts || []);
      setCurrentPage(1);
    } catch (e) {
      setError(e.response?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      fetchReceipts(dateRange);
    }
  }, [dateRange]);

  const districts = useMemo(
    () => ["Все", ...new Set((receipts || []).map((r) => r.district || "").filter(Boolean))],
    [receipts]
  );

  const filtered = useMemo(
    () =>
      districtFilter === "Все"
        ? receipts
        : receipts.filter((r) => r.district === districtFilter),
    [receipts, districtFilter]
  );

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;

  const paginated = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filtered.slice(startIndex, startIndex + pageSize);
  }, [filtered, currentPage, pageSize]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const renderProductsBlock = (products = []) => {
    if (!products.length) return null;

    return (
      <div className={styles.productsBlock}>
        {products.map((p, i) => {
          const qty = Number(p.actual_quantity || 0);
          const price = Number(p.price || 0);
          const sum = qty * price;
          return (
            <div className={styles.productItem} key={i}>
              <span className={styles.productName}>{p.product_name}</span>
              <span className={styles.productQty}>× {qty}</span>
              <span className={styles.productPrice}>
                {price ? price.toLocaleString() : ""}
              </span>
              <span className={styles.productSum}>
                {sum ? sum.toLocaleString() : ""}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTableView = () => (
    <>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Номер</th>
              <th>Дата</th>
              <th>Время</th>
              <th>Район</th>
              <th>Адрес</th>
              <th>Товары</th> {/* ← новая колонка */}
              <th className={styles.right}>Сумма</th>
              <th className={styles.right}>К оплате</th>
              <th className={styles.right}>Оплачено</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((r) => (
              <tr key={r.receipt_number}>
                <td>{r.receipt_number}</td>
                <td>{formatDate(r.delivery_date)}</td>
                <td>{r.delivery_time}</td>
                <td>
                  <span className={styles.districtBadge}>{r.district || "—"}</span>
                </td>
                <td title={r.delivery_address}>{r.delivery_address}</td>

                {/* товары как одна ячейка в строке */}
                <td>{renderProductsBlock(r.products)}</td>

                <td className={styles.right}>
                  {r.amount != null ? Number(r.amount).toLocaleString() : ""}
                </td>
                <td className={styles.right}>
                  {r.to_pay != null ? Number(r.to_pay).toLocaleString() : ""}
                </td>
                <td className={styles.right}>
                  {r.paid != null ? Number(r.paid).toLocaleString() : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filtered.length > pageSize && (
        <div className={styles.pagination}>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Пред.
          </button>
          <span>
            Стр. {currentPage} из {totalPages}
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            След.
          </button>
        </div>
      )}
    </>
  );


  const renderCardsView = () => (
    <div className={styles.cardsWrapper}>
      {paginated.map((r) => (
        <div key={r.receipt_number} className={styles.receiptCard}>
          <div className={styles.receiptHeader}>
            <span className={styles.receiptNumber}>
              <FaFileInvoice /> #{r.receipt_number}
            </span>
            <span className={styles.receiptDate}>
              <FaClock /> {formatDate(r.delivery_date)} {r.delivery_time}
            </span>
          </div>

          <div className={styles.receiptBody}>
            <p>
              <span className={styles.districtBadgeBig}>
                <FaMapMarkerAlt /> {r.district || "—"}
              </span>
            </p>
            <p>
              <FaUser /> <b>Клиент:</b> {r.client_name}
            </p>
            <p>
              <FaMapMarkerAlt /> <b>Адрес:</b> {r.delivery_address}
            </p>
            <p>
              <FaMoneyBillWave /> <b>Сумма:</b>{" "}
              {r.amount != null ? Number(r.amount).toLocaleString() : ""}
            </p>
            <p>
              <b>К оплате:</b>{" "}
              {r.to_pay != null ? Number(r.to_pay).toLocaleString() : ""}{" "}
              &nbsp; | &nbsp;
              <b>Оплачено:</b>{" "}
              {r.paid != null ? Number(r.paid).toLocaleString() : ""}
            </p>
            {r.delivery_comment && (
              <p className={styles.comment}>
                <b>Комментарий:</b> {r.delivery_comment}
              </p>
            )}
          </div>

          {renderProductsBlock(r.products)}
        </div>
      ))}

      {filtered.length > pageSize && (
        <div className={styles.pagination}>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Пред.
          </button>
          <span>
            Стр. {currentPage} из {totalPages}
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            След.
          </button>
        </div>
      )}
    </div>
  );

  return (
    <PageLayout
      header={
        <Header
          icon={FaFileInvoice}
          title="Квитанции"
          subtitle="Просмотр квитанций за период"
        />
      }
      controls={
        <>
          <DateRangePicker
            onChange={(value) => {
              if (value?.from && value?.to) {
                setDateRange(value);
              }
            }}
          />

          <div className={styles.filtersRow}>
            <div className={styles.viewSwitch}>
              <button
                className={`${styles.switchBtn} ${
                  viewMode === "cards" ? styles.active : ""
                }`}
                onClick={() => setViewMode("cards")}
              >
                Карточки
              </button>
              <button
                className={`${styles.switchBtn} ${
                  viewMode === "table" ? styles.active : ""
                }`}
                onClick={() => setViewMode("table")}
              >
                Таблица
              </button>
            </div>

            <div className={styles.districtFilter}>
              <span>Район:</span>
              <select
                className={styles.select}
                value={districtFilter}
                onChange={(e) => {
                  setDistrictFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                {districts.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      }
    >
      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <p className={styles.loadingText}>Загрузка...</p>
      ) : filtered.length === 0 ? (
        <p className={styles.noData}>Нет квитанций за выбранный период</p>
      ) : viewMode === "table" ? (
        renderTableView()
      ) : (
        renderCardsView()
      )}
    </PageLayout>
  );
}
